/**
 * gamepad_ride.ts — the pad → ride-input mapping, pure and testable.
 *
 * Analog throttle and analog steering are not a nicety on a racing vehicle: the
 * whole speed-sensitive steering curve exists to compensate for BINARY keys. A
 * stick can hold a line a key cannot.
 *
 * Everything here is a pure function of a `PadLike` snapshot, so the harness can
 * drive an Xbox pad, a Bluetooth DualSense reporting a non-standard layout, and
 * a Firefox pad with binary triggers without a browser.
 *
 * ── What the research says we have to survive (2026-09-16) ────────────────────
 * · SECURE CONTEXT. `navigator.getGamepads` is restricted to https:// (and
 *   localhost) in Chrome, Edge and Firefox 81+. On an insecure page it is
 *   absent or returns nothing — the caller must degrade, never throw.
 * · A USER GESTURE IS REQUIRED. To blunt fingerprinting, `getGamepads()` returns
 *   nulls and `gamepadconnected` never fires until the player presses a button or
 *   moves a stick WHILE THE PAGE IS FOCUSED. A pad that is already plugged in at
 *   load looks exactly like no pad at all. So: poll every tick, never latch
 *   "there is no pad" at startup, and say so in the UI rather than in a warning.
 * · `pad.id` IS NOT A KEY. Windows XInput exposes no vendor/product id and macOS
 *   labels controllers generically; the same pad reports different id strings in
 *   Firefox and Chrome. Branch on CAPABILITY — `mapping`, axis count, button
 *   count — never on a parsed name.
 * · THE STANDARD LAYOUT is the only one the spec defines: axes[0,1] left stick,
 *   axes[2,3] right stick; buttons 0-3 faces, 4/5 bumpers, 6/7 triggers, 8/9
 *   select/start, 12-15 d-pad. Xbox, DualShock 4, DualSense and (on current
 *   browsers) the Switch Pro all report it over USB.
 * · NON-STANDARD IS REAL. A DualShock 4 / DualSense over BLUETOOTH can report
 *   `mapping: ""` with shifted button indices and the triggers relocated into
 *   `axes`, where they rest at −1 rather than 0. `padTriggerAxes` finds those by
 *   their RESTING VALUE instead of guessing a driver-specific index.
 * · FIREFOX MAY REPORT TRIGGERS AS BINARY (bugzilla 1434408): `value` jumps
 *   0 → 1 with nothing in between. `padButtonValue` also honours `pressed`, so a
 *   pad whose `value` never moves still drives — just without the fine control.
 * · THE SWITCH PRO PUTS B AT INDEX 0. Indices are positional, not lettered; we
 *   describe the bindings by position for that reason.
 * · CHROMIUM MASKS AXES until they pass through a neutral zone below 0.1, so a
 *   drifting stick can read a flat 0 until it is centred once. Nothing to do
 *   about it, but do not mistake it for a dead pad.
 *
 * Sources are listed in `atlas/docs/2026-09-16-vehicle-physics-review.md` §9.
 */

/** The shape we need from a `GamepadButton` — a plain object in the harness. */
export interface PadButtonLike { readonly pressed: boolean; readonly value: number }

/** The shape we need from a `Gamepad`. */
export interface PadLike {
  readonly mapping?: string;
  readonly axes: readonly number[];
  readonly buttons: readonly PadButtonLike[];
  readonly connected?: boolean;
  readonly id?: string;
  readonly index?: number;
}

/** Inner deadzone on a stick axis, RESCALED so the full travel is preserved. */
export const PAD_STICK_DEADZONE = 0.12;
/** Inner deadzone on a trigger (they rest dirtier than sticks on some pads). */
export const PAD_TRIGGER_DEADZONE = 0.06;
/**
 * Steering linearity: 1 is a straight 1:1 response, 0 is a pure cube. Racing
 * games ship a curve like this because a pad's stick is ~2 cm of travel doing
 * the job of half a metre of wheel — biasing the first part of the travel to
 * turn LESS is what buys fine control near centre, which is the whole point of
 * a stick over a key. 0.45 keeps full lock reachable at full deflection.
 */
export const PAD_STEER_LINEARITY = 0.45;
/** An axis resting at or below this is a TRIGGER pretending to be an axis. */
export const PAD_TRIGGER_REST_MAX = -0.75;

/** Standard-layout indices, by POSITION (the Switch Pro's index 0 is "B"). */
export const PAD_BTN = {
  faceDown: 0, faceRight: 1, faceLeft: 2, faceUp: 3,
  bumperL: 4, bumperR: 5, triggerL: 6, triggerR: 7,
} as const;

/** What the ride actually asks for — the same channels the keys produce. */
export interface PadRide {
  /** −1 (full reverse/brake) … +1 (full throttle), analog. */
  throttle: number;
  /** −1 (full right) … +1 (full left), analog, signed like the A/D keys. */
  steer: number;
  boost: boolean;
  handbrake: boolean;
  /** True when the pad is actually asking for something this tick. */
  active: boolean;
}

/** Capability profile, measured from the pad — never parsed from its name. */
export interface PadProfile {
  standard: boolean;
  axisCount: number;
  buttonCount: number;
  /** Axes that rest at ≈ −1: triggers relocated out of `buttons` (Bluetooth DS4). */
  triggerAxes: readonly number[];
  /** True when SOME source on this pad can give a continuous throttle. */
  analogThrottle: boolean;
}

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

/**
 * Scaled radial deadzone on ONE axis: below the deadzone the axis is dead, and
 * the remaining travel is stretched back over the full 0..1 range so no
 * sensitivity is lost at the low end (the "scaled" form — an unscaled deadzone
 * just shrinks the usable range).
 */
export function padAxis(v: number, dz: number = PAD_STICK_DEADZONE): number {
  if (!Number.isFinite(v)) return 0;
  const a = Math.abs(v);
  if (a <= dz) return 0;
  return Math.sign(v) * Math.min(1, (a - dz) / (1 - dz));
}

/** Blend of linear and cubic response — see `PAD_STEER_LINEARITY`. */
export function padSteerCurve(v: number, linearity: number = PAD_STEER_LINEARITY): number {
  const l = clamp01(linearity);
  return v * (l + (1 - l) * v * v);
}

/**
 * A button's analog position. Firefox can leave `value` at 0 while reporting
 * `pressed`, so a pad that cannot do analog still reads as fully on rather than
 * as nothing at all.
 */
export function padButtonValue(b: PadButtonLike | undefined): number {
  if (!b) return 0;
  if (Number.isFinite(b.value) && b.value > 0) return clamp01(b.value);
  return b.pressed ? 1 : 0;
}

/**
 * Which axes are really triggers, found by their RESTING value rather than by a
 * driver-specific index. `rest` is a snapshot taken while no button was pressed.
 */
export function padTriggerAxes(rest: readonly number[]): number[] {
  const out: number[] = [];
  for (let i = 0; i < rest.length; i++) if (Number.isFinite(rest[i]) && rest[i] <= PAD_TRIGGER_REST_MAX) out.push(i);
  return out;
}

/** Measure a pad's capabilities once; `rest` may be null until a quiet frame. */
export function padProfile(pad: PadLike, rest: readonly number[] | null): PadProfile {
  const standard = pad.mapping === 'standard';
  const buttonCount = pad.buttons.length;
  const triggerAxes = !standard && buttonCount < 7 && rest ? padTriggerAxes(rest) : [];
  // Whether the throttle can be continuous at all. A pad with no trigger buttons
  // and no trigger axes drives off face buttons, which are on/off by nature.
  // (Firefox's binary-`value` quirk cannot be seen from one frame, so this is
  // the STRUCTURAL case only — the honest one we can answer.)
  const analogThrottle = buttonCount >= 8 || triggerAxes.length >= 2;
  return { standard, axisCount: pad.axes.length, buttonCount, triggerAxes, analogThrottle };
}

/** The two trigger positions, 0..1 each, however this pad happens to expose them. */
export function padTriggers(pad: PadLike, prof: PadProfile): { lt: number; rt: number } {
  // 1. The standard layout, and most non-standard ones too: buttons 6 and 7.
  if (prof.buttonCount >= 8) {
    return {
      lt: padAxis(padButtonValue(pad.buttons[PAD_BTN.triggerL]), PAD_TRIGGER_DEADZONE),
      rt: padAxis(padButtonValue(pad.buttons[PAD_BTN.triggerR]), PAD_TRIGGER_DEADZONE),
    };
  }
  // 2. Triggers relocated into `axes`, resting at −1 (Bluetooth DualShock/DualSense
  //    on some drivers). Normalise [−1, 1] → [0, 1].
  if (prof.triggerAxes.length >= 2) {
    const [a, b] = prof.triggerAxes;
    const norm = (i: number): number => padAxis(clamp01(((pad.axes[i] ?? -1) + 1) / 2), PAD_TRIGGER_DEADZONE);
    return { lt: norm(a), rt: norm(b) };
  }
  // 3. No triggers at all — the face buttons drive (a minimal or exotic pad).
  //    faceRight is left free: it is the handbrake, and one button cannot be both.
  return {
    lt: padButtonValue(pad.buttons[PAD_BTN.faceLeft]),
    rt: padButtonValue(pad.buttons[PAD_BTN.faceDown]),
  };
}

/** Is this button down? Tolerates a pad with fewer buttons than the layout. */
export function padDown(pad: PadLike, ...idx: number[]): boolean {
  for (const i of idx) { const b = pad.buttons[i]; if (b && (b.pressed || b.value > 0.5)) return true; }
  return false;
}

/**
 * The full mapping. Right trigger throttles, left trigger brakes and reverses,
 * the left stick steers, a shoulder boosts and a face button handbrakes.
 *
 * `steer` comes out signed like the A/D keys (+1 = left) because the model's
 * yaw convention has yaw increasing to the left; a stick pushed RIGHT therefore
 * produces a NEGATIVE steer.
 */
export function padRide(pad: PadLike | null | undefined, prof: PadProfile | null): PadRide {
  const idle: PadRide = { throttle: 0, steer: 0, boost: false, handbrake: false, active: false };
  if (!pad || pad.connected === false || !prof) return idle;
  const { lt, rt } = padTriggers(pad, prof);
  const throttle = Math.max(-1, Math.min(1, rt - lt));
  const stick = padAxis(pad.axes[0] ?? 0);
  const steer = -padSteerCurve(stick);
  // Boost: right bumper or the upper face button. Handbrake: left bumper or the
  // right face button. Both are doubled so a pad missing bumpers still plays.
  const boost = padDown(pad, PAD_BTN.bumperR, PAD_BTN.faceUp);
  const handbrake = padDown(pad, PAD_BTN.bumperL, PAD_BTN.faceRight);
  const active = throttle !== 0 || steer !== 0 || boost || handbrake;
  return { throttle, steer, boost, handbrake, active };
}
